import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Building2, Newspaper, CalendarDays, FileText } from 'lucide-react'

type DayCount = { day: string; count: number }

function bucketByDay(rows: { date: string }[], days: number): DayCount[] {
  const buckets: Record<string, number> = {}
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date()
    d.setDate(d.getDate() - i)
    buckets[d.toISOString().slice(0, 10)] = 0
  }
  rows.forEach(row => {
    const day = row.date?.slice(0, 10)
    if (day in buckets) buckets[day] += 1
  })
  return Object.entries(buckets).map(([day, count]) => ({ day, count }))
}

function Sparkline({ data }: { data: DayCount[] }) {
  const max = Math.max(1, ...data.map(d => d.count))
  return (
    <div className="flex items-end gap-1 h-8 mt-2">
      {data.map(d => (
        <div
          key={d.day}
          className="flex-1 bg-[#C8A84B]/30 rounded-sm"
          style={{ height: `${Math.max(8, (d.count / max) * 100)}%` }}
          title={`${d.day}: ${d.count}`}
        />
      ))}
    </div>
  )
}

export function PlatformAnalyticsView() {
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()

  const { data: growth } = useQuery({
    queryKey: ['platform-growth-7d'],
    queryFn: async () => {
      const [orgs, journalists, events, articles] = await Promise.all([
        supabase.from('organizations').select('created_at, settings')
          .not('settings->>is_platform_owner', 'eq', 'true')
          .gte('created_at', sevenDaysAgo),
        supabase.from('journalists').select('created_at').gte('created_at', sevenDaysAgo),
        supabase.from('events').select('created_at').gte('created_at', sevenDaysAgo),
        supabase.from('coverage_articles').select('submitted_at').gte('submitted_at', sevenDaysAgo),
      ])
      return {
        institutions: bucketByDay((orgs.data ?? []).map(r => ({ date: r.created_at })), 7),
        journalists: bucketByDay((journalists.data ?? []).map(r => ({ date: r.created_at })), 7),
        events: bucketByDay((events.data ?? []).map(r => ({ date: r.created_at })), 7),
        articles: bucketByDay((articles.data ?? []).map(r => ({ date: r.submitted_at })), 7),
      }
    }
  })

  const { data: orgsWithStats } = useQuery({
    queryKey: ['orgs-with-stats'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations')
        .select(`
          id, name, slug, created_at,
          org_journalist_stats(*),
          org_event_stats(*),
          org_coverage_stats(*)
        `)
        .not('settings->>is_platform_owner', 'eq', 'true') as any)
      return (data || []) as any[]
    }
  })

  // Last active per org, derived from the most recent activity_log row
  const { data: lastActiveByOrg } = useQuery({
    queryKey: ['platform-last-active'],
    queryFn: async () => {
      const { data } = await supabase
        .from('activity_log')
        .select('org_id, created_at')
        .order('created_at', { ascending: false })
        .limit(500)
      const map: Record<string, string> = {}
      ;(data ?? []).forEach(row => {
        if (!map[row.org_id]) map[row.org_id] = row.created_at
      })
      return map
    }
  })

  // Platform-wide journalist leaderboard (top 10 by avg coverage score)
  const { data: leaderboard } = useQuery({
    queryKey: ['platform-journalist-leaderboard'],
    queryFn: async () => {
      const [journalistsRes, articlesRes, orgsRes] = await Promise.all([
        supabase.from('journalists').select('id, first_name, last_name, org_id').limit(500),
        supabase.from('coverage_articles').select('journalist_id, total_score').limit(2000),
        supabase.from('organizations').select('id, name'),
      ])
      const orgNameById: Record<string, string> = {}
      ;(orgsRes.data ?? []).forEach(o => { orgNameById[o.id] = o.name })

      const scoreMap: Record<string, { count: number; total: number }> = {}
      ;(articlesRes.data ?? []).forEach(a => {
        if (!scoreMap[a.journalist_id]) scoreMap[a.journalist_id] = { count: 0, total: 0 }
        scoreMap[a.journalist_id].count += 1
        scoreMap[a.journalist_id].total += a.total_score || 0
      })

      return (journalistsRes.data ?? [])
        .map(j => {
          const stats = scoreMap[j.id] ?? { count: 0, total: 0 }
          const avgScore = stats.count > 0 ? Math.round(stats.total / stats.count) : 0
          return { ...j, avgScore, articleCount: stats.count, orgName: orgNameById[j.org_id] ?? '' }
        })
        .filter(j => j.articleCount > 0)
        .sort((a, b) => b.avgScore - a.avgScore)
        .slice(0, 10)
    }
  })

  const { data: usage30d } = useQuery({
    queryKey: ['platform-usage-30d'],
    queryFn: async () => {
      const { data } = await supabase
        .from('activity_log')
        .select('created_at')
        .gte('created_at', thirtyDaysAgo)
      return bucketByDay((data ?? []).map(r => ({ date: r.created_at })), 30)
    }
  })

  const maxUsage = Math.max(1, ...(usage30d ?? []).map(d => d.count))
  const maxAvgScore = Math.max(1, ...(orgsWithStats ?? []).map((o: any) => o.org_coverage_stats?.[0]?.avg_score ?? 0))

  const totalCount = (arr?: DayCount[]) => (arr ?? []).reduce((s, d) => s + d.count, 0)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Platform Analytics</h1>
        <p className="text-justify text-muted-foreground text-sm mt-1">Platform-wide growth, coverage quality, and usage trends.</p>
      </div>

      {/* Row 1, Growth metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'New Institutions', data: growth?.institutions, icon: Building2 },
          { label: 'New Journalists', data: growth?.journalists, icon: Newspaper },
          { label: 'New Events', data: growth?.events, icon: CalendarDays },
          { label: 'New Submissions', data: growth?.articles, icon: FileText },
        ].map(card => {
          const Icon = card.icon
          return (
            <Card key={card.label}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{card.label}</span>
                  <Icon className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="text-2xl font-bold">{totalCount(card.data)}</div>
                <p className="text-justify text-[10px] text-muted-foreground">last 7 days</p>
                {card.data && <Sparkline data={card.data} />}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Row 2, Coverage quality */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Avg Coverage Score by Institution</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(orgsWithStats ?? []).length === 0 ? (
              <p className="text-justify text-sm text-muted-foreground">No institutions yet.</p>
            ) : (orgsWithStats ?? []).map((org: any) => {
              const avgScore = org.org_coverage_stats?.[0]?.avg_score ?? 0
              return (
                <div key={org.id} className="flex items-center gap-2">
                  <span className="text-xs w-32 truncate shrink-0">{org.name}</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#C8A84B]"
                      style={{ width: `${Math.round((avgScore / maxAvgScore) * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground w-8 text-right">{avgScore}</span>
                </div>
              )
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Journalists (Platform-wide)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {(leaderboard ?? []).length === 0 ? (
                  <tr><td className="px-4 py-6 text-center text-muted-foreground text-sm">No coverage data yet.</td></tr>
                ) : (leaderboard ?? []).map((j: any, idx: number) => (
                  <tr key={j.id} className="border-b last:border-0">
                    <td className="px-4 py-2 text-xs font-bold text-muted-foreground w-6">{idx + 1}</td>
                    <td className="px-2 py-2">
                      <div className="text-sm font-medium">{j.first_name} {j.last_name}</div>
                      <div className="text-[11px] text-muted-foreground">{j.orgName}</div>
                    </td>
                    <td className="px-2 py-2 text-right text-sm font-medium" style={{ color: '#C8A84B' }}>{j.avgScore}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Row 3, Activity by institution */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Activity by Institution</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-left">
                <th className="px-4 py-2.5 font-medium text-muted-foreground">Institution</th>
                <th className="px-2 py-2.5 font-medium text-muted-foreground text-right">Journalists</th>
                <th className="px-2 py-2.5 font-medium text-muted-foreground text-right">Events</th>
                <th className="px-2 py-2.5 font-medium text-muted-foreground text-right">Articles</th>
                <th className="px-2 py-2.5 font-medium text-muted-foreground text-right">Avg Score</th>
                <th className="px-2 py-2.5 font-medium text-muted-foreground">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {(orgsWithStats ?? []).length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No institutions yet.</td></tr>
              ) : (orgsWithStats ?? []).map((org: any) => {
                const lastActive = lastActiveByOrg?.[org.id]
                return (
                  <tr key={org.id} className="border-b last:border-0">
                    <td className="px-4 py-2.5 font-medium">{org.name}</td>
                    <td className="px-2 py-2.5 text-right">{org.org_journalist_stats?.[0]?.total_journalists ?? 0}</td>
                    <td className="px-2 py-2.5 text-right">{org.org_event_stats?.[0]?.total_events ?? 0}</td>
                    <td className="px-2 py-2.5 text-right">{org.org_coverage_stats?.[0]?.total_articles ?? 0}</td>
                    <td className="px-2 py-2.5 text-right">{org.org_coverage_stats?.[0]?.avg_score ?? 0}</td>
                    <td className="px-2 py-2.5 text-muted-foreground text-xs">
                      {lastActive ? new Date(lastActive).toLocaleDateString() : ''}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Row 4, Platform usage over time */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Platform Usage (30 days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-32">
            {(usage30d ?? []).map(d => (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.count}`}>
                <div
                  className="w-full bg-[#0D2240]/20 rounded-sm"
                  style={{ height: `${Math.max(4, (d.count / maxUsage) * 100)}%` }}
                />
              </div>
            ))}
          </div>
          <p className="text-justify text-[11px] text-muted-foreground mt-2">Daily activity_log entries across all institutions, last 30 days.</p>
        </CardContent>
      </Card>
    </div>
  )
}
